import {Subscription} from "rxjs";
import {Location} from "@angular/common";
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router, RouterLink} from "@angular/router";

import {appRoutes} from "@numizmat/constants";
import {ScrollerService} from "@numizmat/services/common";
import {SidebarMenuComponent, SidebarMenuItem} from "@numizmat/components";

import {GeneralInfoComponent} from "./components/general-info/general-info.component";
import {LoginSecurityComponent} from "./components/login-security/login-security.component";
import {ProfileSettingsComponent} from "./components/profile-settings/profile-settings.component";
import {AdManagementComponent} from "./components/ad-management/ad-management.component";
import {PurchasesSalesComponent} from "./components/purchases-sales/purchases-sales.component";
import {ChargesPaymentsComponent} from "./components/charges-payments/charges-payments.component";

export interface SectionItem {
  sectionId: number;
  sectionName: string,
  sectionIcon: string;
  sidebarMenuItems: SidebarMenuItem[];
}

@Component({
  selector: 'nz-help-information-page',
  standalone: true,
  imports: [
    RouterLink,
    SidebarMenuComponent,
    GeneralInfoComponent,
    LoginSecurityComponent,
    ProfileSettingsComponent,
    AdManagementComponent,
    PurchasesSalesComponent,
    ChargesPaymentsComponent
  ],
  templateUrl: './help-information-page.component.html',
  styleUrl: './help-information-page.component.scss'
})
export class HelpInformationPageComponent implements OnInit, OnDestroy {
  private router: Router = inject(Router);
  private location: Location = inject(Location);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private scrollerService: ScrollerService = inject(ScrollerService);

  private subs: Subscription = new Subscription();

  protected sectionId?: number;
  protected subsectionId?: number;
  protected sections: SectionItem[] = this.initializeSections();
  protected selectedSection?: SectionItem;
  protected selectedSectionItem?: SidebarMenuItem;
  protected readonly appRoutes = appRoutes;

  public ngOnInit(): void {
    this.createQueryParamsChangeSubscription();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected goBack(): void {
    this.location.back();
  }

  private createQueryParamsChangeSubscription(): void {
    const subscription: Subscription = this.activatedRoute.queryParams.subscribe({
      next: (queryParams: Params) => {
        const sectionId = queryParams['sectionId'];
        const subsectionId = queryParams['subsectionId'];

        if (!!sectionId && !!subsectionId) {
          this.sectionId = Number.parseInt(sectionId);
          this.subsectionId = Number.parseInt(subsectionId);
          const sectionIndex: number = this.sections.findIndex(x => x.sectionId === this.sectionId);
          this.selectedSection = this.sections[sectionIndex];
          const subsectionIndex: number = this.selectedSection.sidebarMenuItems.findIndex(x => x.id === this.subsectionId);
          this.selectedSectionItem = this.selectedSection.sidebarMenuItems[subsectionIndex];
          this.scrollerService.scrollToTop();
        } else {
          this.sectionId = undefined;
          this.subsectionId = undefined;
          this.selectedSection = undefined;
          this.selectedSectionItem = undefined;
        }
      }
    });
    this.subs.add(subscription);
  }

  private initializeSections(): SectionItem[] {
    const currentRoute: string = this.router.url.split('?')[0];
    return [
      {
        sectionId: 100,
        sectionName: "Общи понятия и функционалности",
        sectionIcon: 'fa-solid fa-circle-info',
        sidebarMenuItems: [
          {
            id: 101,
            title: 'Кои сме ние?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '100', 'subsectionId': '101'}
          },
          {
            id: 102,
            title: 'Как работят отзивите?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '100', 'subsectionId': '102'}
          },
          {
            id: 103,
            title: 'Как се формира потребителският рейтинг?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '100', 'subsectionId': '103'}
          },
          {
            id: 104,
            title: 'Какво представляват публичните коментари?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '100', 'subsectionId': '104'}
          },
          {
            id: 105,
            title: 'Как да харесам обява?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '100', 'subsectionId': '105'}
          },
          {
            id: 106,
            title: 'Как да последвам потребител?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '100', 'subsectionId': '106'}
          },
          {
            id: 107,
            title: 'Как да блокирам потребител?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '100', 'subsectionId': '107'}
          }
        ]
      },
      {
        sectionId: 200,
        sectionName: "Вход, регистрация и сигурност",
        sectionIcon: 'fa-solid fa-user-shield',
        sidebarMenuItems: [
          {
            id: 201,
            title: 'Как да вляза в профила си?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '200', 'subsectionId': '201'}
          },
          {
            id: 202,
            title: 'Как да изляза от профила си?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '200', 'subsectionId': '202'}
          },
          {
            id: 203,
            title: 'Как да създам профил (регистрация)?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '200', 'subsectionId': '203'}
          },
          {
            id: 204,
            title: 'Как да възстановя достъпа си (забравена парола)?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '200', 'subsectionId': '204'}
          },
          {
            id: 205,
            title: 'Как да защитя профила си?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '200', 'subsectionId': '205'}
          },
          {
            id: 206,
            title: 'Как да съобщя за нередност или измама?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '200', 'subsectionId': '206'}
          }
        ]
      },
      {
        sectionId: 300,
        sectionName: "Управление на профила",
        sectionIcon: 'fa-solid fa-user-gear',
        sidebarMenuItems: [
          {
            id: 301,
            title: 'Как да сменя паролата си?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '300', 'subsectionId': '301'}
          },
          {
            id: 302,
            title: 'Как да сменя имейл адреса си?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '300', 'subsectionId': '302'}
          },
          {
            id: 303,
            title: 'Как да сменя потребителското си име?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '300', 'subsectionId': '303'}
          },
          {
            id: 304,
            title: 'Как да сменя адреса по подразбиране?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '300', 'subsectionId': '304'}
          },
          {
            id: 305,
            title: 'Как да променя метода на потвърждение?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '300', 'subsectionId': '305'}
          },
          {
            id: 306,
            title: 'Как да управлявам известията си?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '300', 'subsectionId': '306'}
          },
          {
            id: 307,
            title: 'Как да изтрия профила си?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '300', 'subsectionId': '307'}
          }
        ]
      },
      {
        sectionId: 400,
        sectionName: "Управление на обяви",
        sectionIcon: 'fa-solid fa-rectangle-ad',
        sidebarMenuItems: [
          {
            id: 401,
            title: 'Как да добавя обява?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '400', 'subsectionId': '401'}
          },
          {
            id: 402,
            title: 'Как да редактирам обява?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '400', 'subsectionId': '402'}
          },
          {
            id: 403,
            title: 'Как да обновя обява?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '400', 'subsectionId': '403'}
          },
          {
            id: 404,
            title: 'Как да копирам като нова обява?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '400', 'subsectionId': '404'}
          },
          {
            id: 405,
            title: 'Как да спра обява?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '400', 'subsectionId': '405'}
          },
          {
            id: 406,
            title: 'Как да изтрия обява?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '400', 'subsectionId': '406'}
          }
        ]
      },
      {
        sectionId: 500,
        sectionName: "Покупки и продажби",
        sectionIcon: 'fa-solid fa-handshake',
        sidebarMenuItems: [
          {
            id: 501,
            title: 'Как да купя артикул?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '500', 'subsectionId': '501'}
          },
          {
            id: 502,
            title: 'Как да участвам в наддаване?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '500', 'subsectionId': '502'}
          },
          {
            id: 503,
            title: 'Как да изпратя ценово предложение?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '500', 'subsectionId': '503'}
          },
          {
            id: 504,
            title: 'Как да управлявам ценовите предложения?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '500', 'subsectionId': '504'}
          },
          {
            id: 505,
            title: 'Как да се свържа с продавача?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '500', 'subsectionId': '505'}
          },
          {
            id: 506,
            title: 'Как да се свържа с купувача?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '500', 'subsectionId': '506'}
          }
        ]
      },
      {
        sectionId: 600,
        sectionName: "Такси и плащания",
        sectionIcon: 'fa-solid fa-credit-card',
        sidebarMenuItems: [
          {
            id: 601,
            title: 'Какви са таксите в сайта?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '600', 'subsectionId': '601'}
          },
          {
            id: 602,
            title: 'Как да захраня акаунта си?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '600', 'subsectionId': '602'}
          },
          {
            id: 603,
            title: 'Как да поискам възстановяване на средства?',
            routerLink: [currentRoute],
            queryParams: {'sectionId': '600', 'subsectionId': '603'}
          }
        ]
      }
    ]
  }
}
